// Reusable Founder Ops timeline component.
//
// Renders a list of FounderOpsEvent rows in reverse-chronological order.
// The component itself does not fetch — callers pass `events` so the same
// list can be reused for Mission Control, deep-dive pages, filtered
// subsets, etc.
//
// Layout guarantees:
//   • long titles / descriptions truncate rather than wrap out of the card
//   • unresolved events show a small "OPEN" badge next to severity
//   • severity + category get their own colour dots + pills

import type { CSSProperties } from 'react';
import type {
  FounderOpsEvent,
  FounderOpsEventSeverity,
  FounderOpsEventCategory,
} from '../lib/founderOpsEvents';
import { COLORS, RADIUS } from '../theme';
import { relativeTime } from '../lib/format';

export interface FounderOpsTimelineProps {
  events:     FounderOpsEvent[];
  loading?:   boolean;
  error?:     string | null;
  emptyText?: string;
  showResolved?: boolean;   // default: true — display an "OPEN" pill for unresolved
  compact?:   boolean;      // slightly tighter rows for dense contexts
}

export default function FounderOpsTimeline({
  events,
  loading,
  error,
  emptyText = 'No events yet.',
  showResolved = true,
  compact = false,
}: FounderOpsTimelineProps) {
  if (loading) return <div style={stateStyle}>Loading events…</div>;
  if (error)   return <div style={{ ...stateStyle, color: COLORS.critical }}>{error}</div>;
  if (events.length === 0) return <div style={stateStyle}>{emptyText}</div>;

  return (
    <div>
      {events.map((e) => <TimelineRow key={e.id} event={e} showResolved={showResolved} compact={compact} />)}
    </div>
  );
}

function TimelineRow({ event, showResolved, compact }: { event: FounderOpsEvent; showResolved: boolean; compact: boolean }) {
  const sColor = severityColor(event.severity);
  return (
    <div style={compact ? rowCompactStyle : rowStyle}>
      <div style={{ ...dotStyle, background: sColor }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: COLORS.textMuted, fontSize: 11, minWidth: 60 }}>
            {relativeTime(event.created_at)}
          </span>
          <SeverityPill severity={event.severity} />
          <CategoryPill category={event.category} />
          {showResolved && !event.resolved && <OpenPill />}
        </div>
        <div style={titleStyle} title={event.title}>{event.title}</div>
        {event.description && (
          <div style={descStyle} title={event.description}>{event.description}</div>
        )}
        <div style={metaStyle}>
          <span>{event.source}</span>
          {event.actor_type && event.actor_id && (
            <span> · {event.actor_type}:{event.actor_id}</span>
          )}
          {event.correlation_id && (
            <span> · corr {event.correlation_id.slice(0, 12)}</span>
          )}
          {event.resolved && event.resolved_at && (
            <span> · resolved {relativeTime(event.resolved_at)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function SeverityPill({ severity }: { severity: FounderOpsEventSeverity }) {
  const c = severityColor(severity);
  return (
    <span style={{
      padding: '1px 6px', borderRadius: RADIUS.chip,
      background: c + '1a', color: c, border: `1px solid ${c}55`,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>{severity}</span>
  );
}

function CategoryPill({ category }: { category: FounderOpsEventCategory }) {
  return (
    <span style={{
      padding: '1px 6px', borderRadius: RADIUS.chip,
      background: COLORS.surfaceAlt, color: COLORS.textSecondary,
      border: `1px solid ${COLORS.border}`,
      fontSize: 10, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>{category}</span>
  );
}

function OpenPill() {
  return (
    <span style={{
      padding: '1px 6px', borderRadius: RADIUS.chip,
      background: COLORS.warn + '2a', color: COLORS.warn, border: `1px solid ${COLORS.warn}55`,
      fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>open</span>
  );
}

export function severityColor(s: FounderOpsEventSeverity): string {
  switch (s) {
    case 'info':     return COLORS.info;
    case 'success':  return COLORS.success;
    case 'warning':  return COLORS.warn;
    case 'error':    return '#F78B4B';
    case 'critical': return COLORS.critical;
    default:         return COLORS.textMuted;
  }
}

// ── Styles ────────────────────────────────────────────────────────────────

const stateStyle: CSSProperties = {
  color: COLORS.textMuted, fontSize: 13, padding: '10px 4px',
};
const rowStyle: CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12,
  padding: '12px 0', borderBottom: `1px dashed ${COLORS.border}`,
};
const rowCompactStyle: CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 10,
  padding: '8px 0', borderBottom: `1px dashed ${COLORS.border}`,
};
const dotStyle: CSSProperties = {
  width: 8, height: 8, borderRadius: 4, flexShrink: 0, marginTop: 8,
};
const titleStyle: CSSProperties = {
  color: COLORS.text, fontSize: 13, fontWeight: 500, marginTop: 4,
  overflow: 'hidden', textOverflow: 'ellipsis',
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
};
const descStyle: CSSProperties = {
  color: COLORS.textSecondary, fontSize: 12, lineHeight: 1.5, marginTop: 2,
  overflow: 'hidden', textOverflow: 'ellipsis',
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
};
const metaStyle: CSSProperties = {
  color: COLORS.textMuted, fontSize: 11, marginTop: 4,
};
